import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Plus, Layers } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { PageLoader } from '../../components/PageLoader'
import { EmptyState } from '../../components/EmptyState'
import PullToRefresh from '../../components/PullToRefresh'
import { usePhases } from '../../hooks/usePhases'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { performRefresh } from '../../lib/refresh'
import { PhaseForm } from './components/PhaseForm'
import { PhaseCard } from './components/PhaseCard'
import QuarterlyGoals from './components/QuarterlyGoals'

export function PhasesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goBack = () => navigate('/')
  const { phases, loading, createPhase, updatePhase, deletePhase, refetch } = usePhases()
  const confirm = useConfirm()
  const handleRefresh = useCallback(() => performRefresh([refetch]), [refetch])
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const today = new Date().toLocaleDateString('en-CA')
  const isCurrent = (p) => {
    if (!p?.start_date) return false
    if (p.start_date > today) return false
    if (!p.end_date) return true
    return p.end_date >= today
  }

  const handleCreate = async (values) => {
    setSubmitting(true)
    const ok = await createPhase(values)
    setSubmitting(false)
    if (ok) setCreating(false)
    return ok
  }

  const handleUpdate = async (values) => {
    if (!editing) return false
    setSubmitting(true)
    const ok = await updatePhase(editing.id, values)
    setSubmitting(false)
    if (ok) setEditing(null)
    return ok
  }

  const handleDelete = async (phase) => {
    const ok = await confirm({
      title: t('phoenixTracker.phases.confirmDelete'),
      description: t('phoenixTracker.phases.confirmDeleteDesc'),
      confirmLabel: t('phoenixTracker.phases.delete'),
      variant: 'destructive',
    })
    if (ok) await deletePhase(phase.id)
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-foreground">{t('phoenixTracker.phases.title')}</h1>
          <p className="text-xs text-muted-foreground">{t('phoenixTracker.phases.subtitle')}</p>
        </div>
        {!creating && !editing && (
          <Button
            onClick={() => setCreating(true)}
            size="sm"
            className="bg-app-primary-500 hover:bg-app-primary-600 text-white"
          >
            <Plus className="w-4 h-4 me-1" />
            {t('phoenixTracker.phases.addCta')}
          </Button>
        )}
      </div>

      <QuarterlyGoals />

      {creating && (
        <Card>
          <CardContent className="p-4">
            <PhaseForm
              onSubmit={handleCreate}
              onCancel={() => setCreating(false)}
              submitting={submitting}
            />
          </CardContent>
        </Card>
      )}

      {editing && (
        <Card>
          <CardContent className="p-4">
            <PhaseForm
              initialValues={editing}
              onSubmit={handleUpdate}
              onCancel={() => setEditing(null)}
              submitting={submitting}
            />
          </CardContent>
        </Card>
      )}

      {loading && <PageLoader compact />}

      {!loading && phases.length === 0 && !creating && (
        <EmptyState
          icon={Layers}
          title={t('phoenixTracker.phases.empty')}
          action={{
            label: t('phoenixTracker.phases.addCta'),
            onClick: () => setCreating(true),
          }}
        />
      )}

      {!loading && phases.length > 0 && (
        <div className="space-y-2">
          {phases.map((phase) => (
            <PhaseCard
              key={phase.id}
              phase={phase}
              isCurrent={isCurrent(phase)}
              onEdit={(p) => setEditing(p)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
    </PullToRefresh>
  )
}
